import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, vModelCheckbox as _vModelCheckbox, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "document-upload-form__group" }
const _hoisted_2 = { class: "document-upload-form__label" }
const _hoisted_3 = { value: "" }
const _hoisted_4 = { value: "devis" }
const _hoisted_5 = { value: "facture" }
const _hoisted_6 = { value: "plan" }
const _hoisted_7 = { value: "recu" }
const _hoisted_8 = { value: "contrat" }
const _hoisted_9 = { value: "autre" }
const _hoisted_10 = { class: "document-upload-form__group" }
const _hoisted_11 = { class: "document-upload-form__label" }
const _hoisted_12 = { class: "document-upload-form__file-drop" }
const _hoisted_13 = { class: "document-upload-form__file-text" }
const _hoisted_14 = { class: "document-upload-form__file-title" }
const _hoisted_15 = { class: "document-upload-form__file-subtitle" }
const _hoisted_16 = {
  key: 0,
  class: "document-upload-form__selected-files"
}
const _hoisted_17 = { class: "document-upload-form__file-info" }
const _hoisted_18 = { class: "document-upload-form__file-name" }
const _hoisted_19 = { class: "document-upload-form__file-size" }
const _hoisted_20 = ["onClick", "aria-label"]
const _hoisted_21 = {
  key: 1,
  class: "document-upload-form__error"
}
const _hoisted_22 = { class: "document-upload-form__group" }
const _hoisted_23 = { class: "document-upload-form__label" }
const _hoisted_24 = ["placeholder"]
const _hoisted_25 = {
  key: 0,
  class: "document-upload-form__client-toggle"
}
const _hoisted_26 = { class: "document-upload-form__client-toggle-label" }
const _hoisted_27 = { class: "document-upload-form__client-toggle-control" }
const _hoisted_28 = { class: "toggle-switch" }
const _hoisted_29 = { class: "document-upload-form__actions" }
const _hoisted_30 = {
  key: 0,
  class: "fas fa-upload",
  "aria-hidden": "true"
}

import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';
import AppButton from '../../base/AppButton.vue';
import AppModal from '../../base/Modal.vue';
import { sanitizeFileNames } from '@/utils/fileNameSanitizer';

// Props
interface Props {
  projectId?: string | number | null;
  show: boolean;
  uploading?: boolean;
  maxFileSize?: number;
  allowedTypes?: string[];
  showClientVisibilityToggle?: boolean;
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (e: 'upload', formData: FormData): void;
  (e: 'upload-success'): void;
  (e: 'upload-error', error: string): void;
}

const acceptedFileTypes = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt';
const MAX_FILES = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentUploadModal',
  props: {
    projectId: { default: null },
    show: { type: Boolean, default: false },
    uploading: { type: Boolean, default: false },
    maxFileSize: { default: 10 * 1024 * 1024 },
    allowedTypes: { default: () => ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff'] },
    showClientVisibilityToggle: { type: Boolean, default: false }
  },
  emits: ["update:show", "upload", "upload-success", "upload-error"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;

// Emits
const emit = __emit;

const { t } = useI18n();
const { notifyError } = useNotifications();

const documentType = ref('');
const description = ref('');
const selectedFiles = ref<File[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);
const fileError = ref('');
const isDragging = ref(false);
const sharedWithClient = ref(true);

const isValid = computed(() => {
  return documentType.value && selectedFiles.value.length > 0 && !fileError.value;
});

function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

const validateFile = (file: File) => {
  fileError.value = '';
    if (!file) {
    const errorMsg = t('documents.selectFile');
    fileError.value = errorMsg;
    notifyError(errorMsg);
    return false;
  }

  if (file.size > props.maxFileSize) {
    const errorMsg = t('documents.fileSizeLimit', { size: props.maxFileSize / (1024 * 1024) });
    fileError.value = errorMsg;
    notifyError(errorMsg);
    return false;
  }

  const fileExtension = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
  if (!acceptedFileTypes.includes(fileExtension)) {
    const errorMsg = t('documents.invalidFileType', { types: acceptedFileTypes });
    fileError.value = errorMsg;
    notifyError(errorMsg);
    return false;
  }

  return true;
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target.files) return;
  
  const files = Array.from(target.files);
  const validFiles = [];
  
  fileError.value = '';
  
  for (const file of files) {
    if (validateFile(file)) {
      validFiles.push(file);
    }
  }
  
  if (validFiles.length > 0) {
    const remaining = MAX_FILES - selectedFiles.value.length;
    const toAdd = validFiles.slice(0, remaining);
    if (validFiles.length > remaining) {
      const errorMsg = t('documents.tooManyFiles', { max: MAX_FILES });
      fileError.value = errorMsg;
      notifyError(errorMsg);
    }
    if (toAdd.length > 0) {
      const sanitizedFiles = sanitizeFileNames(toAdd);
      selectedFiles.value = [...selectedFiles.value, ...sanitizedFiles];
    }
  }
  
  // Reset input to allow selecting the same files again if needed
  target.value = '';
};

const removeFile = (index: number) => {
  selectedFiles.value.splice(index, 1);
  fileError.value = '';
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = false;
};

const handleDrop = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = false;
  
  const files = event.dataTransfer?.files;
  if (!files) return;
  
  const validFiles: File[] = [];
  fileError.value = '';
  
  for (const file of Array.from(files)) {
    if (validateFile(file)) {
      validFiles.push(file);
    }
  }
  
  if (validFiles.length > 0) {
    const remaining = MAX_FILES - selectedFiles.value.length;
    const toAdd = validFiles.slice(0, remaining);
    if (validFiles.length > remaining) {
      const errorMsg = t('documents.tooManyFiles', { max: MAX_FILES });
      fileError.value = errorMsg;
      notifyError(errorMsg);
    }
    if (toAdd.length > 0) {
      const sanitizedFiles = sanitizeFileNames(toAdd);
      selectedFiles.value = [...selectedFiles.value, ...sanitizedFiles];
    }
  }
};

const handleSubmit = () => {
  if (!isValid.value) return;
  if (props.uploading) return;

  const formData = new FormData();
  
  // Append each file to the FormData
  selectedFiles.value.forEach((file: File, index: number) => {
    console.log(`Uploading file ${index + 1}: ${file.name}`);
    formData.append('documents', file);
  });
  
  formData.append('documentType', documentType.value);
  formData.append('description', description.value);
  formData.append('sharedWithClient', String(sharedWithClient.value));

  emit('upload', formData);
};

// Reset form when modal is closed
const resetForm = () => {
  documentType.value = '';
  description.value = '';
  selectedFiles.value = [];
  fileError.value = '';
  isDragging.value = false;
  sharedWithClient.value = true;
  
  // Reset file input
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

// Watch for modal show prop changes to reset form when modal is opened
watch(() => props.show, (newValue: boolean, oldValue: boolean) => {
  if (newValue && !oldValue) {
    // Modal is being opened, reset the form
    resetForm();
  }
});

// Handle modal close - reset form and emit close
const handleClose = () => {
  resetForm();
  emit('close');
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AppModal, {
    show: _ctx.show,
    onClose: handleClose,
    title: _ctx.$t('documents.uploadDocuments'),
    size: "medium",
    "show-close": true
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_29, [
        _createVNode(AppButton, {
          type: "button",
          variant: "outline",
          onClick: handleClose,
          disabled: props.uploading
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.cancel')), 1)
          ]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(AppButton, {
          type: "button",
          variant: "primary",
          onClick: handleSubmit,
          disabled: !isValid.value || props.uploading,
          loading: props.uploading
        }, {
          default: _withCtx(() => [
            (!props.uploading)
              ? (_openBlock(), _createElementBlock("i", _hoisted_30))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('documents.upload')), 1)
          ]),
          _: 1
        }, 8, ["disabled", "loading"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("form", {
        onSubmit: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"])),
        class: "document-upload-form"
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("label", _hoisted_2, _toDisplayString(_ctx.$t('documents.documentType')), 1),
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((documentType).value = $event)),
            class: "document-upload-form__select",
            required: ""
          }, [
            _createElementVNode("option", _hoisted_3, _toDisplayString(_ctx.$t('common.selectOption')), 1),
            _createElementVNode("option", _hoisted_4, _toDisplayString(_ctx.$t('documents.types.devis')), 1),
            _createElementVNode("option", _hoisted_5, _toDisplayString(_ctx.$t('documents.types.facture')), 1),
            _createElementVNode("option", _hoisted_6, _toDisplayString(_ctx.$t('documents.types.plan')), 1),
            _createElementVNode("option", _hoisted_7, _toDisplayString(_ctx.$t('documents.types.recu')), 1),
            _createElementVNode("option", _hoisted_8, _toDisplayString(_ctx.$t('documents.types.contrat')), 1),
            _createElementVNode("option", _hoisted_9, _toDisplayString(_ctx.$t('documents.types.autre')), 1)
          ], 512), [
            [_vModelSelect, documentType.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createElementVNode("label", _hoisted_11, _toDisplayString(_ctx.$t('documents.files')), 1),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("input", {
              type: "file",
              ref_key: "fileInput",
              ref: fileInput,
              onChange: handleFileChange,
              class: "document-upload-form__file-input",
              accept: acceptedFileTypes,
              multiple: ""
            }, null, 544),
            _cache[6] || (_cache[6] = _createTextVNode()),
            _createElementVNode("div", {
              class: _normalizeClass(["document-upload-form__file-area", { 'document-upload-form__file-area--dragging': isDragging.value }]),
              onClick: _cache[1] || (_cache[1] = () => _ctx.$refs.fileInput?.click()),
              onDragover: _withModifiers(handleDragOver, ["prevent"]),
              onDragleave: _withModifiers(handleDragLeave, ["prevent"]),
              onDrop: _withModifiers(handleDrop, ["prevent"])
            }, [
              _cache[5] || (_cache[5] = _createElementVNode("div", { class: "document-upload-form__file-icon" }, [
                _createElementVNode("i", { class: "fas fa-cloud-upload-alt" })
              ], -1)),
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("p", _hoisted_14, _toDisplayString(selectedFiles.value.length > 0 ? `${selectedFiles.value.length} ${_ctx.$t('documents.filesSelected')}` : _ctx.$t('documents.dragDropFiles')), 1),
                _createElementVNode("p", _hoisted_15, _toDisplayString(_ctx.$t('documents.supportedFormats')) + ": PDF, JPG, PNG, DOC, DOCX ", 1)
              ])
            ], 34)
          ]),
          (selectedFiles.value.length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedFiles.value, (file, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: index,
                    class: "document-upload-form__file-item"
                  }, [
                    _createElementVNode("div", _hoisted_17, [
                      _cache[7] || (_cache[7] = _createElementVNode("i", {
                        class: "fas fa-file",
                        "aria-hidden": "true"
                      }, null, -1)),
                      _createElementVNode("span", _hoisted_18, _toDisplayString(file.name), 1),
                      _createElementVNode("span", _hoisted_19, _toDisplayString(formatFileSize(file.size)), 1)
                    ]),
                    _createElementVNode("button", {
                      type: "button",
                      onClick: ($event: any) => (removeFile(index)),
                      class: "document-upload-form__remove-file",
                      "aria-label": _ctx.$t('documents.removeFile')
                    }, [...(_cache[8] || (_cache[8] = [
                      _createElementVNode("i", {
                        class: "fas fa-times",
                        "aria-hidden": "true"
                      }, null, -1)
                    ]))], 8, _hoisted_20)
                  ]))
                }), 128))
              ]))
            : _createCommentVNode("", true),
          (fileError.value)
            ? (_openBlock(), _createElementBlock("small", _hoisted_21, _toDisplayString(fileError.value), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_22, [
          _createElementVNode("label", _hoisted_23, _toDisplayString(_ctx.$t('documents.description')), 1),
          _withDirectives(_createElementVNode("textarea", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((description).value = $event)),
            class: "document-upload-form__textarea",
            placeholder: _ctx.$t('documents.descriptionPlaceholder'),
            rows: "4"
          }, null, 8, _hoisted_24), [
            [_vModelText, description.value]
          ])
        ]),
        (props.showClientVisibilityToggle)
          ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
              _createElementVNode("span", _hoisted_26, _toDisplayString(_ctx.$t('documents.sharedWithClient')), 1),
              _createElementVNode("div", _hoisted_27, [
                _createElementVNode("label", _hoisted_28, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((sharedWithClient).value = $event))
                  }, null, 512), [
                    [_vModelCheckbox, sharedWithClient.value]
                  ]),
                  _cache[9] || (_cache[9] = _createElementVNode("span", { class: "toggle-slider" }, null, -1))
                ]),
                _createElementVNode("span", {
                  class: _normalizeClass(["document-upload-form__toggle-hint", sharedWithClient.value ? 'hint-visible' : 'hint-hidden'])
                }, _toDisplayString(sharedWithClient.value ? _ctx.$t('documents.visibleToClient') : _ctx.$t('documents.hiddenFromClient')), 3)
              ])
            ]))
          : _createCommentVNode("", true)
      ], 32)
    ]),
    _: 1
  }, 8, ["show", "title"]))
}
}

})